import { gzip } from 'pako';
import { CreateReportRunUploadUrlResult } from '@blisspointmedia/bpm-types/dist/DigitalAudit';
import { awaitJSON, DigitalAuditLambdaFetch, fetchPresigned } from '@src/utils/fetch-utils';
import { AuditReport } from './digital-audit-constants';
import {
  CreateReportRunInput,
  CreateReportRunResult,
  ReportRunDetail,
  ReportRunSummary
} from './report-runs-constants';

// Mirrors bpm-lambdas' lib/bpmWrapper.ts's MAX_BODY_SIZE (5,500,000 bytes) - the raw-byte
// threshold past which a Lambda response needs compression to have a chance of fitting under the
// synchronous invoke's hard ~6MB ceiling. The same ceiling applies in reverse to a POST body: an
// uncompressed request this large risks failing at the transport layer before createReportRun's
// handler ever runs. Keep in sync if bpm-lambdas' constant changes.
const MAX_INLINE_REPORT_BYTES = 5_500_000;

export const listReportRuns = async (domain: string): Promise<ReportRunSummary[]> => {
  const res = await DigitalAuditLambdaFetch(
    `/digital-audit/report-runs?domain=${encodeURIComponent(domain)}`,
    { method: 'GET' }
  );
  return awaitJSON<ReportRunSummary[]>(res);
};

// The raw wire shape getReportRun can return - `report_url` is an internal transport detail
// (bpm-lambdas always offloads the report to S3 and hands back a presigned URL instead of returning
// it inline) that this module resolves before handing a plain ReportRunDetail to every other
// caller.
interface ReportRunDetailWire extends Omit<ReportRunDetail, 'report'> {
  report: AuditReport | null;
  report_url?: string | null;
}

export const getReportRun = async (runId: string): Promise<ReportRunDetail> => {
  const res = await DigitalAuditLambdaFetch(`/digital-audit/report-runs/${runId}`, {
    method: 'GET'
  });
  const { report_url: reportUrl, ...run } = await awaitJSON<ReportRunDetailWire>(res);
  if (!reportUrl) {
    // Only reachable against a backend that predates the always-offload change, which still
    // returned `report` inline.
    return { ...run, report: run.report as AuditReport };
  }
  // Fetched directly from S3, not through DigitalAuditLambdaFetch - no auth header needed, same as
  // this app's other direct-to-S3 reads (e.g. S3SignedUrlFetch).
  const report = await awaitJSON<AuditReport>(
    await fetchPresigned(reportUrl, 'Report download failed')
  );
  return { ...run, report };
};

const mintReportRunUploadUrl = async (): Promise<CreateReportRunUploadUrlResult> => {
  const res = await DigitalAuditLambdaFetch('/digital-audit/report-runs/upload-url', {
    method: 'POST'
  });
  return awaitJSON<CreateReportRunUploadUrlResult>(res);
};

export const createReportRun = async (
  input: CreateReportRunInput
): Promise<CreateReportRunResult> => {
  const reportJson = JSON.stringify(input.report);
  // UTF-8 byte length, not string.length (UTF-16 code units) - the two diverge for non-ASCII
  // page content, and this needs to track the same byte count the backend's own size check uses.
  const reportBytes = new TextEncoder().encode(reportJson).length;
  if (reportBytes <= MAX_INLINE_REPORT_BYTES) {
    const res = await DigitalAuditLambdaFetch('/digital-audit/report-runs', {
      method: 'POST',
      body: input
    });
    return awaitJSON<CreateReportRunResult>(res);
  }

  // Too large to send inline - upload directly to S3 (bytes never pass through a lambda, same
  // three-round-trip shape as Oracle Onboarding's uploadSourceDocument: mint a presigned URL, PUT
  // the bytes to it, then commit) and pass the resulting key instead of the report itself.
  const { key, uploadUrl } = await mintReportRunUploadUrl();
  await fetchPresigned(uploadUrl, 'Report upload failed', {
    method: 'PUT',
    body: gzip(reportJson),
    headers: { 'Content-Type': 'application/json' }
  });
  const res = await DigitalAuditLambdaFetch('/digital-audit/report-runs', {
    method: 'POST',
    body: { url: input.url, scan_source: input.scan_source, report_key: key }
  });
  return awaitJSON<CreateReportRunResult>(res);
};
